/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-param-reassign */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropType from 'prop-types';
import UserDatabases from '@Services/UserDatabases';
import QueryOptionsDropdown from '@Pages/QueryBuilder/QueryOptionsDropdown/QueryOptionsDropdown';
import * as Suggestions from '@Services/Suggestions';
import { config } from '@Constants/rx-ui/constants';
import htmlStripsService from '@Services/HtmlStripsService';
import { dispatchOpenMolecularModal } from '@Containers/MolecularModal/MolecularModalActions';
import Molecular from '@Services/Molecular';
import className from 'classnames';
import debounce from 'lodash/debounce';
import map from 'lodash/map';
import memoize from 'lodash/memoize';
import omit from 'lodash/omit';
import { Autosuggest } from '@els/els-react--autosuggest';
import { useDispatch, useSelector } from 'react-redux';
import { updateValueofQuery, onPropUnSelectAction } from '@Pages/QueryBuilder/QueryletActions';
import { nodesList } from '@Pages/QueryBuilder/QuerySelector';
import { ButtonWithIcon } from '@els/els-react--button';
import { Flyout } from '@els/els-react--flyout';
import MolecularFormulaLink from '../../../MolecularFormulaLink/MolecularFormulaLink';
import { focusNextDragButtonOrSidePanel } from '../../../Shared/focusManagement';
import ShowMoreOptions from './ShowMoreOptions/ShowMoreOptions';
import { NumericType, opsType, WordType } from '../constant';
import takeWordForSearchingPure from './FieldTypeHelper';

function FieldType({ field, type, node, boundOps }) {
  const userDbId = UserDatabases.getDefaultDatabase()?.id;
  const dispatch = useDispatch();
  const nodesArray = useSelector(nodesList);
  const [fieldData, setfieldData] = useState({
    data: { ...field },
    minQueryLength: config.BUILD_BY_TYPE_OPTIONS.typeaheadQueryMinLength.stringField,
    suggestConfig: {
      databases: field.databases || [],
      databaseId: field.databaseId || (field.databases && field.databases[0]) || userDbId,
      fieldIds: field.id,
      title: field.displayName,
      queryLet: field,
    },
  });

  const [suggestionList, setSuggestionList] = useState([]);
  const [fromModal, setFromModal] = useState(false);
  const autoSuggestRef = useRef(null);

  useEffect(() => {
    if (type === NumericType) {
      setfieldData((prevData) => ({
        ...prevData,
        data: field.type === 'fact' ? field.children?.[0] : field,
        suggestConfig: {
          databases: field.databases || [],
          databaseId: field.databaseId || (field.databases && field.databases[0]) || userDbId,
          fieldIds: field.id,
          title: field.displayName,
          queryLet: field,
        },
      }));
    }
    autoSuggestRef.current?.setTextInputValue(fieldData.data?.value ?? '');
  }, [field]);

  useEffect(() => {
    Molecular.initialize();
  }, []);

  useEffect(() => {
    let filteredOperations = {};
    if (type === NumericType) {
      const DEPRECATED_OPERATIONS = ['op_num_between'];
      filteredOperations = omit(boundOps?.data?.numeric, DEPRECATED_OPERATIONS);
    } else {
      filteredOperations = boundOps?.data?.string;
    }
    setfieldData((prevData) => ({
      ...prevData,
      operations: filteredOperations,
      data: { ...field, operations: filteredOperations, operation: field.operation || field.defaultBoundOperator || opsType[type] },
      suggestConfig: {
        databases: field.databases || [],
        databaseId: field.databaseId || (field.databases && field.databases[0]) || userDbId,
        fieldIds: field.id,
        title: field.displayName,
        queryLet: field,
      },
    }));
  }, [type]);

  const loadData = (query) => {
    let queryString;
    if (type === WordType) {
      queryString = memoize(takeWordForSearchingPure(query, query.length), (...args) => JSON.stringify(args));
    } else {
      queryString = query;
    }
    return Suggestions.getSuggestions({
      databaseId: fieldData?.suggestConfig?.databaseId,
      matchesCount: 10,
      fieldIds: [fieldData?.data?.id],
      expandedValue: queryString,
      origin: 'QUERYLET',
    })
      .then((data) => {
        if (type !== NumericType) {
          map(data?.[0]?.sub, (item) => {
            item.title = htmlStripsService.getTitleWithoutHtml(item.title, 'stripexceptions');
            item.displayTitle = htmlStripsService.getTitleWithoutHtml(item.title, 'escapeall');
            return item;
          });
        } else {
          map(data?.[0]?.sub, (item) => {
            item.displayTitle = item.title;
            return item.title;
          });
        }
        const suggestions = data?.[0]?.sub?.map((item) => ({ inputValue: item.title }));
        setSuggestionList(suggestions);
      })
      .catch(Logger.reject);
  };

  const debouncedFetchSuggestions = useCallback(debounce(loadData, 500), []);
  const fetchResults = (query) => {
    setFromModal(false);
    dispatch(updateValueofQuery(fieldData.data, node, query, 'value'));
    if (!query || query.length < 1 || query.trim() === '' || fieldData?.data?.id === 'CIT.NLQL') {
      return;
    }
    debouncedFetchSuggestions(query);
  };

  /** Update the value selected from Autosuggest */
  const updatefromSuggestion = (suggestedSelection) => {
    const value = suggestedSelection?.inputValue ?? suggestedSelection?.value ?? '';
    setfieldData((prevData) => ({
      ...prevData,
      data: { ...prevData.data, value },
    }));
    dispatch(updateValueofQuery(fieldData.data, node, value, 'value'));
  };

  /** Update the value selected from Modals */
  const updateInputValue = (displayTitle) => {
    autoSuggestRef.current?.setTextInputValue(displayTitle ?? '');
    setFromModal(true);
    setfieldData((prevData) => ({
      ...prevData,
      data: { ...prevData.data, operation: field.operation, value: displayTitle },
    }));
    dispatch(updateValueofQuery(fieldData.data, node, displayTitle, 'value'));
  };

  const onSelectOperation = (key) => {
    setfieldData((prevData) => ({
      ...prevData,
      operation: key,
      data: { ...prevData.data, operation: key },
    }));
    dispatch(updateValueofQuery(fieldData.data, node, key, 'operation'));
  };

  const OnFormulaBuilderModal = () => {
    dispatchOpenMolecularModal({ updateInputValue });
  };
  const isInputEmpty = !fieldData?.data?.value || fieldData?.data?.value.trim() === '';
  const isNlqField = fieldData?.data?.id === 'CIT.NLQL';

  return (
    <div
      className={className('ql__prop__form', {
        'number-query-search-field__form-right--align': node.openQuerylet,
      })}
      aria-label={
        isNlqField ? undefined : `Truncation operators ${fieldData.operations?.[fieldData.data?.operation]}`
      }>
      {fieldData.data && fieldData.operations && !isNlqField && (
        <QueryOptionsDropdown queryOptionsType={fieldData} handleSelectOperation={onSelectOperation} node={node} />
      )}
      <Autosuggest
        textInputProps={{
          label: `Search text field for ${node.displayName}`,
          placeholder: fieldData.data?.displayName,
          children: null,
          ...(isInputEmpty
            ? {}
            : {
                iconRight: {
                  sprite: 'Close',
                  children: 'Clear input',
                  isDecorative: true,
                },
              }),
          iconRightCallback: isInputEmpty ? undefined : () => fetchResults(''),
          shouldHideLabel: true,
        }}
        shouldAlwaysChooseSuggestion={false}
        shouldHideLabel
        openSuggestionsOnUpdate={!fromModal}
        ref={autoSuggestRef}
        suggestions={suggestionList}
        shouldCloseOnESC
        onChange={(n) => fetchResults(n)}
        onSelectSuggestion={(r, n) => updatefromSuggestion(n || r)}
        className={className('ql__prop__form__input', {
          'ql__prop__form__input--mf': fieldData?.data?.id === 'IDE.MF',
          'ql__prop__form__input--number': node.canExist,
        })}
      />
      <div className="ql__prop__form__icons">
        <div style={{ display: 'flex', gap: '0 1rem' }}>
          {!isNlqField && (
            <Flyout trigger="hover" flyout="Lookup list" placement="bottom" theme="simple">
              <ShowMoreOptions suggestionData={fieldData} updateSuggestedValue={updateInputValue} source />
            </Flyout>
          )}
          {fieldData?.data?.id === 'IDE.MF' && (
            <Flyout trigger="hover" flyout="Molecular Formula" placement="bottom" theme="simple">
              <MolecularFormulaLink onMolecularFormulaChange={updateInputValue} openFormulaBuilderModal={OnFormulaBuilderModal} />
            </Flyout>
          )}
        </div>
        {!node.isImmutable && (!node.items || node?.items?.length === 0) && !node.canExist && node.trackingSearchType !== 'fields' && (
          <Flyout trigger="hover" flyout={<span aria-hidden="true">Delete Querylet</span>} placement="bottom" theme="simple">
            <ButtonWithIcon
              id="querybuilder-delete-querylet"
              sprite="Trash"
              iconOnly
              type="link"
              data-e2e="delete-querylet-button"
              aria-label="Delete Querylet"
              className="cursor-pointer"
              onClick={() => {
                dispatch(onPropUnSelectAction(node.uuid, nodesArray));
                focusNextDragButtonOrSidePanel(nodesArray, node.uuid);
              }}>
              Delete Querylet
            </ButtonWithIcon>
          </Flyout>
        )}
      </div>
    </div>
  );
}

FieldType.propTypes = {
  field: PropType.object,
  type: PropType.string,
  node: PropType.object,
  boundOps: PropType.object,
};

export default FieldType;
